import { PlaceResultTypeEnum } from "@search/api/enums";
import {
  LocationResult,
  PlaceResult,
  RegionResult,
  VenueResult,
} from "@search/api/types";
import { useLocationQuery } from "@search/api/useLocationQuery";
import clsx from "clsx";

import Autocomplete from "@/components/compounds/forms/Autocomplete";
import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./LocationAutocompleteFacet.module.css";

const ref = "location-autocomplete-facet";

interface LocationAutocompleteFacetProps extends Partial<CommonProps> {
  updateLocation: (slug: string) => void;
  updateRegion: (id: number) => void;
}

function useLocationAutocompleteFacet(props: LocationAutocompleteFacetProps) {
  const className = clsx(styles[ref], props.className);
  const { id = createId(ref), updateLocation, updateRegion } = props;

  return { className, id, updateLocation, updateRegion };
}

function LocationAutocompleteFacet(props: LocationAutocompleteFacetProps) {
  const { className, id, updateLocation, updateRegion } =
    useLocationAutocompleteFacet(props);
  const { searchLocations } = useLocationQuery();

  async function search(text: string) {
    if (text) {
      const { data } = await searchLocations(text);

      return data;
    }
  }

  function transform(data: Array<LocationResult>) {
    return data.map((result: any, index: number) => ({ id: index, ...result }));
  }

  function onSelect(item: LocationResult) {
    const { type } = item;

    if (type === PlaceResultTypeEnum.PLACE) {
      const { slug } = item as PlaceResult;

      updateLocation(slug);
    } else if (type === PlaceResultTypeEnum.REGION) {
      const { id } = item as RegionResult;

      updateRegion(id);
    } else if (type === PlaceResultTypeEnum.VENUE) {
      const { slug } = item as VenueResult;

      updateLocation(slug);
    }
  }

  return (
    <div className={className} id={id}>
      <Autocomplete
        color={ColorEnum.DARKGREEN}
        minLength={3}
        noResultsText={"No locations found"}
        search={search}
        size={10}
        transform={transform}
        onUpdate={onSelect}
      />
    </div>
  );
}

export default LocationAutocompleteFacet;
export { LocationAutocompleteFacet, type LocationAutocompleteFacetProps };
